/**
 * Compress videos in the browser when possible.
 * Never starts a multi‑hundred‑MB raw upload (that looks "stuck" at 41%).
 * Oversized / HEVC clips must compress first or the user gets a clear error.
 */
import { FFmpeg } from "@ffmpeg/ffmpeg";

const FFMPEG_CORE_URL =
  "https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/umd";

/** Do not upload originals larger than this — they hang the UI for minutes. */
export const MAX_UPLOAD_MB = 80;

let ffmpeg = null;
let loadingPromise = null;
let lastFfmpegError = "";

async function toBlobURL(url, mimeType) {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to load ${url}: ${res.status}`);
  }
  const buf = await res.arrayBuffer();
  return URL.createObjectURL(new Blob([buf], { type: mimeType }));
}

function resetFfmpeg() {
  ffmpeg = null;
  loadingPromise = null;
}

async function getFFmpeg(onProgress) {
  if (!ffmpeg) {
    ffmpeg = new FFmpeg();
    ffmpeg.on("log", ({ message }) => {
      if (message) lastFfmpegError = String(message);
    });
  }

  if (onProgress) {
    ffmpeg.on("progress", ({ progress }) => {
      const pct = Math.max(0, Math.min(100, Math.round((progress || 0) * 100)));
      onProgress(pct);
    });
  }

  if (!ffmpeg.loaded) {
    if (!loadingPromise) {
      loadingPromise = ffmpeg.load({
        coreURL: await toBlobURL(
          `${FFMPEG_CORE_URL}/ffmpeg-core.js`,
          "text/javascript"
        ),
        wasmURL: await toBlobURL(
          `${FFMPEG_CORE_URL}/ffmpeg-core.wasm`,
          "application/wasm"
        ),
      });
    }
    try {
      await loadingPromise;
    } catch (err) {
      resetFfmpeg();
      throw err;
    } finally {
      loadingPromise = null;
    }
  }

  return ffmpeg;
}

export async function isLikelyHevc(file) {
  try {
    const slice = file.slice(0, Math.min(file.size, 4 * 1024 * 1024));
    const buf = await slice.arrayBuffer();
    const text = new TextDecoder("latin1").decode(buf);
    return /hev1|hvc1|hvcC|dvh1/.test(text);
  } catch {
    return false;
  }
}

export function canBrowserPlayVideo(file) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      video.removeAttribute("src");
      try {
        video.load();
      } catch (_) {
        /* ignore */
      }
      resolve(ok);
    };
    const timer = window.setTimeout(() => finish(false), 8000);
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.onerror = () => finish(false);
    video.onloadeddata = () => {
      finish(video.videoWidth > 0 && video.videoHeight > 0);
    };
    video.src = url;
  });
}

function toMp4File(data, originalName) {
  const blob = new Blob([data], { type: "video/mp4" });
  const base = String(originalName || "video").replace(/\.[^.]+$/, "");
  return new File([blob], `${base}.mp4`, { type: "video/mp4" });
}

async function runEncode(ff, inName, outName, args) {
  lastFfmpegError = "";
  const code = await ff.exec(["-i", inName, ...args, outName]);
  if (code !== 0) {
    throw new Error(lastFfmpegError || `FFmpeg exited with code ${code}`);
  }
}

function needsReencode(file, hevc, playsOk, sizeMb) {
  if (hevc) return true;
  if (!playsOk) return true;
  if (sizeMb >= 25) return true;
  if (!/\.mp4$/i.test(file.name) && file.type !== "video/mp4") return true;
  return false;
}

async function encodeInBrowser(file, onProgress) {
  const sizeMb = file.size / (1024 * 1024);
  const height = sizeMb >= 100 ? "360" : sizeMb >= 40 ? "480" : "720";
  const crf = sizeMb >= 100 ? "32" : sizeMb >= 40 ? "30" : "28";
  const ext = (file.name.split(".").pop() || "mp4").toLowerCase();
  const inName = `input.${ext}`;
  const outName = "output.mp4";

  const attempts = [
    [
      "-vf",
      `scale=-2:${height}`,
      "-c:v",
      "libx264",
      "-pix_fmt",
      "yuv420p",
      "-preset",
      "ultrafast",
      "-crf",
      crf,
      "-c:a",
      "aac",
      "-b:a",
      "96k",
      "-ac",
      "2",
      "-movflags",
      "+faststart",
    ],
    [
      "-vf",
      "scale=-2:360",
      "-r",
      "24",
      "-c:v",
      "libx264",
      "-pix_fmt",
      "yuv420p",
      "-preset",
      "ultrafast",
      "-crf",
      "34",
      "-an",
      "-movflags",
      "+faststart",
    ],
  ];

  let lastError = null;
  for (let i = 0; i < attempts.length; i++) {
    try {
      const ff = await getFFmpeg(onProgress);
      try {
        await ff.deleteFile(inName);
      } catch (_) {}
      try {
        await ff.deleteFile(outName);
      } catch (_) {}

      onProgress?.(Math.round(((i + 0.05) / attempts.length) * 100));
      // ffmpeg.wasm only accepts Uint8Array; a raw ArrayBuffer fails with "Unsupported data type".
      await ff.writeFile(inName, new Uint8Array(await file.arrayBuffer()));
      await runEncode(ff, inName, outName, attempts[i]);
      const data = await ff.readFile(outName);
      try {
        await ff.deleteFile(inName);
      } catch (_) {}
      try {
        await ff.deleteFile(outName);
      } catch (_) {}
      onProgress?.(100);
      return toMp4File(data, file.name);
    } catch (err) {
      lastError = err;
      console.warn(`Video encode attempt ${i + 1} failed:`, err);
      resetFfmpeg();
    }
  }
  throw lastError || new Error(lastFfmpegError || "Browser encode failed");
}

/**
 * @returns {Promise<{ file: File, mode: string, message?: string }>}
 */
export async function compressVideo(file, options = {}) {
  const { onProgress } = options;
  const hevc = await isLikelyHevc(file);
  const sizeMb = file.size / (1024 * 1024);

  onProgress?.(3);
  const playsOk = !hevc && (await canBrowserPlayVideo(file));

  if (!needsReencode(file, hevc, playsOk, sizeMb)) {
    onProgress?.(100);
    return { file, mode: "passthrough" };
  }

  // Very large files: browser often can't load them into WASM.
  // Don't start a 500MB upload that freezes at "Uploading 1%".
  if (sizeMb > 200) {
    throw new Error(
      `This video is too large (${formatBytes(file.size)}) to upload from the browser. ` +
        `On the server run: php repair_videos.php — or compress it first to under ${MAX_UPLOAD_MB}MB (H.264 MP4), then upload.`
    );
  }

  try {
    const compressed = await encodeInBrowser(file, onProgress);
    const outMb = compressed.size / (1024 * 1024);
    if (outMb > MAX_UPLOAD_MB) {
      throw new Error(
        `Compressed video is still ${formatBytes(compressed.size)}. Please use a shorter clip.`
      );
    }
    return {
      file: compressed,
      mode: "compressed",
      message: `Compressed ${formatBytes(file.size)} → ${formatBytes(compressed.size)}`,
    };
  } catch (err) {
    // If original is small enough, let the server convert (ffmpeg on production).
    if (sizeMb <= MAX_UPLOAD_MB) {
      onProgress?.(100);
      return {
        file,
        mode: "server",
        message:
          `Uploading ${formatBytes(file.size)} — server will convert to H.264. This may take a minute…`,
      };
    }
    throw new Error(
      (err && err.message) ||
        `Could not compress this video in the browser. Export as H.264 MP4 under ${MAX_UPLOAD_MB}MB, or run php repair_videos.php on the server.`
    );
  }
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 100) return `${Math.round(mb)} MB`;
  if (mb >= 10) return `${mb.toFixed(1)} MB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
